import React, { useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import applicationService from '@/shared/services/applicationService';
import ActionBar from '@/components/common/ActionBar';

const COPY = {
    revise: {
        button: 'Revise this request',
        title: 'Revise this request?',
        confirm: 'Start revising',
        working: 'Starting…',
        failure:
            'The request could not be opened for revision. Please try again.',
        body: (courseLabel) => (
            <>
                <p>
                    Your request for {courseLabel} stays on file, but programs
                    cannot decide it while you revise it. Resubmit it when you
                    are done and the programs are told it changed.
                </p>
                <p className="mb-0">
                    This is available until a program records a decision. After
                    that, ask the program coordinator.
                </p>
            </>
        ),
    },
    reopen: {
        button: 'Reopen',
        title: 'Reopen this request?',
        confirm: 'Reopen',
        working: 'Reopening…',
        failure: 'The request could not be reopened. Please try again.',
        body: (courseLabel) => (
            <>
                <p>
                    Your withdrawn request for {courseLabel} will open for
                    revision with everything you entered. Programs review it
                    again once you resubmit it.
                </p>
                <p className="mb-0">
                    Your CRN comes back if no other request holds it this term.
                    The application window must still be open.
                </p>
            </>
        ),
    },
};

/**
 * Takes the faculty member's own request back into revision, after a
 * confirmation: 'revise' from a submitted request, 'reopen' from a withdrawn
 * one. onStarted gets the server's answer and may throw to show a message in
 * the dialog.
 */
function StartRevisionButton({
    mode,
    applicationId,
    courseLabel,
    onStarted,
    onRefused,
    size,
    variant = 'outline-primary',
}) {
    const copy = COPY[mode];
    const [show, setShow] = useState(false);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    const [stale, setStale] = useState(false);
    const ariaLabel =
        mode === 'reopen'
            ? `Reopen ${courseLabel} application`
            : `Revise this request for ${courseLabel}`;
    const titleId = `${mode}-request-title-${applicationId}`;

    const close = () => {
        if (working) return;
        setShow(false);
        setError(null);
        if (stale) {
            setStale(false);
            onRefused?.();
        }
    };

    const confirm = async () => {
        setWorking(true);
        setError(null);
        try {
            const send =
                mode === 'reopen'
                    ? applicationService.reopenApplication
                    : applicationService.reviseApplication;
            const result = await send(applicationId);
            await onStarted?.(result);
            setShow(false);
        } catch (err) {
            // The page reloads the request once the message is closed. Until
            // then the same press would only get the same refusal
            setStale(true);
            setError(err.response?.data?.message || copy.failure);
        } finally {
            setWorking(false);
        }
    };

    return (
        <>
            <Button
                variant={variant}
                size={size}
                onClick={() => setShow(true)}
                aria-label={ariaLabel}
            >
                {copy.button}
            </Button>
            <Modal show={show} onHide={close} aria-labelledby={titleId}>
                <Modal.Header closeButton>
                    <Modal.Title id={titleId} as="h2" className="h5">
                        {copy.title}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {copy.body(courseLabel)}
                    {error && (
                        <Alert variant="danger" className="mt-3 mb-0">
                            {error}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <ActionBar
                        className="w-100"
                        secondary={
                            <Button
                                variant="outline-primary"
                                onClick={close}
                                disabled={working}
                            >
                                Cancel
                            </Button>
                        }
                        primary={
                            <Button
                                variant="primary"
                                onClick={confirm}
                                disabled={working || stale}
                            >
                                {working ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                            aria-hidden="true"
                                        />
                                        {copy.working}
                                    </>
                                ) : (
                                    copy.confirm
                                )}
                            </Button>
                        }
                    />
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default StartRevisionButton;
